import { useEffect } from 'react';

import { Logger } from '../../logger/Logger';
import { useCoreDispatch } from '../../store/CoreStore';
import { settingsActions } from '../../store/slices/settings/slice';
import { useLegacyContext } from '../legacyMode/LegacyContextProvider';

// Very Old Browsers and new iPads
interface WebkitDocument extends Document {
  webkitFullscreenElement?: HTMLElement;
  webkitExitFullscreen: () => Promise<void>;
}

// Very Old Browsers and new iPads
interface WebkitElement extends HTMLElement {
  webkitRequestFullscreen: () => Promise<void>;
}

export const toggleFullscreen = (isLegacyMode?: boolean): void => {
  const windowObject = isLegacyMode ? window.parent.document : window.document;

  // Close FullScreen
  if (windowObject.fullscreenElement) {
    void windowObject.exitFullscreen();
  } else if ((windowObject as WebkitDocument).webkitFullscreenElement) {
    void (windowObject as WebkitDocument).webkitExitFullscreen();
  } else {
    // Request FullScreen
    Logger.logEvent('openFullscreen');
    const element = windowObject.documentElement;
    if (element.requestFullscreen) {
      void element.requestFullscreen();
    } else if ((element as WebkitElement).webkitRequestFullscreen) {
      void (element as WebkitElement).webkitRequestFullscreen();
    }
  }
};

export const useFullscreen = (): void => {
  const dispatch = useCoreDispatch();
  const isLegacyMode = useLegacyContext().isLegacyMode;
  const windowObject = isLegacyMode ? window.parent.document : window.document;

  useEffect(() => {
    const handleFullscreenChange = (): void => {
      dispatch(
        settingsActions.setIsInFullscreen(
          !!windowObject.fullscreenElement || !!(windowObject as WebkitDocument).webkitFullscreenElement
        )
      );
    };

    const handleFullscreenError = (): void => {
      Logger.logFailure('fullscreen', {
        additional: 'Failed to open FullScreen',
      });
    };

    windowObject.addEventListener('fullscreenchange', handleFullscreenChange);
    windowObject.addEventListener('fullscreenerror', handleFullscreenError);
    windowObject.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return (): void => {
      windowObject.removeEventListener('fullscreenchange', handleFullscreenChange);
      windowObject.removeEventListener('fullscreenerror', handleFullscreenError);
      windowObject.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [dispatch]);
};
